// src/pages/UsersImport.js - Version finale avec indicatifs obligatoires
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs
const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  accent: '#4FC3F7',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3'
};

// ✅ Fonction pour convertir le libellé de profil en code numérique
const convertProfileToCode = (profile) => {
  // Si c'est déjà un chiffre valide (pour rétrocompatibilité)
  if (profile === '2' || profile === '4') {
    return profile;
  }
  
  // Sinon on convertit le mot en code
  const profileMap = {
    'controleur': '2',
    'contrôleur': '2',
    'controlleur': '2',
    'agent': '4'
  };
  
  const profileLower = profile?.toString().toLowerCase().trim() || '';
  return profileMap[profileLower] || '';
};

// ✅ Fonction pour convertir le statut en code
const convertStatusToCode = (statut) => {
  // Si c'est déjà un chiffre valide
  if (statut === '0' || statut === '1') {
    return statut;
  }
  
  // Sinon on convertit le mot en code
  const statusMap = {
    'actif': '1',
    'inactif': '0'
  };
  
  const statusLower = statut?.toString().toLowerCase().trim() || '';
  return statusMap[statusLower] || '1'; // Par défaut actif
};

// ✅ Fonction pour normaliser le numéro de téléphone (indicatif OBLIGATOIRE)
const normalizePhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Supprimer les espaces
  let cleaned = phone.toString().trim().replace(/\s/g, '');
  
  // 🔥 DOIT commencer par +
  if (!cleaned.startsWith('+')) {
    throw new Error('Le numéro de téléphone doit commencer par + (indicatif international). Exemple: +22712345678');
  }
  
  // Vérifier qu'après le + il n'y a que des chiffres
  if (!/^\+\d+$/.test(cleaned)) {
    throw new Error('Format de téléphone invalide. Utilisez uniquement des chiffres après le +. Exemple: +22712345678');
  }
  
  return cleaned;
};

const UsersImport = () => {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [currentSocieteId, setCurrentSocieteId] = useState(null);
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Results

  // Charger les infos utilisateur
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUserInfo(userData);
        setCurrentSocieteId(userData.societe_id || userData.id_societe || null);
        
        // Vérifier que c'est un gestionnaire (profil 3)
        if (String(userData.profile).trim() !== "3") {
          setError("Seuls les gestionnaires peuvent importer des utilisateurs.");
        }
      } catch (error) {
        navigate("/login");
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  // Parser les lignes CSV
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  };

  // Lire le fichier CSV
  const parseCSV = (text) => {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return [];
    
    // Ignorer les lignes de commentaires
    const dataLines = lines.filter(line => !line.trim().startsWith('#'));
    
    // Lire les en-têtes
    const headers = dataLines[0]
      .split(',')
      .map(h => h.trim())
      .map(h => h.toLowerCase());
    
    // ✅ En-têtes requis
    const requiredHeaders = ['nom', 'prenom', 'email', 'profile'];
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    
    if (missingHeaders.length > 0) {
      throw new Error(`En-têtes manquants: ${missingHeaders.join(', ')}. Vérifiez le template.`);
    }
    
    const result = [];
    
    // Traiter chaque ligne de données
    for (let i = 1; i < dataLines.length; i++) {
      const line = dataLines[i].trim();
      
      if (!line) continue;
      
      // Parser la ligne CSV
      const values = parseCSVLine(line);
      
      if (values.length !== headers.length) {
        console.warn(`Ligne ${i+1}: Nombre de colonnes incorrect, ignorée`);
        continue;
      }
      
      const obj = {};
      headers.forEach((header, index) => {
        obj[header] = values[index] || '';
      });
      
      result.push(obj);
    }
    
    return result;
  };

  // ========== FONCTION TÉLÉCHARGER MODÈLE - AVEC INDICATIFS ==========
  const downloadTemplate = () => {
    const headers = [
      'nom',
      'prenom',
      'email',
      'phone',          // ← Doit contenir +227...
      'matricule',
      'profile',
      'statut'
    ];

    // ✅ Exemples avec indicatifs obligatoires
    const examples = [
      ['Dupont', 'Jean', 'jean.dupont@exemple.com', '+22712345678', 'CTRL001', 'Contrôleur', 'actif'],
      ['Martin', 'Marie', 'marie.martin@exemple.com', '+22798765432', 'AGNT001', 'Agent', 'actif'],
      ['Durand', 'Pierre', 'pierre.durand@exemple.com', '+22790123456', 'CTRL002', 'Contrôleur', 'inactif'],
      ['Leroy', 'Sophie', 'sophie.leroy@exemple.com', '+22723456789', 'AGNT002', 'Agent', 'actif'],
      ['Petit', 'Luc', 'luc.petit@exemple.com', '+22734567890', 'AGNT003', 'Agent', 'actif']
    ];

    const BOM = "\uFEFF";
    
    let csvContent = headers.join(',') + '\n';
    
    examples.forEach(row => {
      csvContent += row.join(',') + '\n';
    });

    const blob = new Blob([BOM + csvContent], { 
      type: 'text/csv;charset=utf-8;' 
    });
    
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modele_import_utilisateurs.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Gestionnaire de fichier
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setError("");
    setPreviewData([]);
    setImportResults(null);
    setStep(1);

    if (!selectedFile) return;

    // Vérifier l'extension
    const fileExt = selectedFile.name.split('.').pop().toLowerCase();
    if (fileExt !== 'csv') {
      setError("Format de fichier non supporté. Utilisez uniquement CSV.");
      setFile(null);
      return;
    }

    // Vérifier la taille
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError("Fichier trop volumineux (max 10 Mo).");
      setFile(null);
      return;
    }

    // Lire le fichier
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const data = parseCSV(text);
        
        // Limiter à 50 lignes pour la prévisualisation
        const preview = data.slice(0, 50);
        setPreviewData(preview);
        setStep(2);
        
        if (preview.length === 0) {
          setError("Aucune donnée trouvée dans le fichier. Vérifiez le format.");
          setFile(null);
        }
      } catch (err) {
        console.error("Erreur lecture fichier:", err);
        setError(err.message || "Erreur lors de la lecture du fichier.");
        setFile(null);
      }
    };

    reader.readAsText(selectedFile, 'UTF-8');
  };

  // ✅ Validation des données (avec indicatif OBLIGATOIRE)
  const validateData = (data) => {
    const errors = [];
    const emails = new Set();
    
    // Profils autorisés (en mots ou en chiffres)
    const validProfiles = ['controleur', 'contrôleur', 'controlleur', 'agent'];
    
    data.forEach((row, index) => {
      const line = index + 2;
      
      // Nom
      if (!row.nom || row.nom.toString().trim() === '') {
        errors.push(`Ligne ${line}: Nom manquant`);
      }
      
      // Prénom
      if (!row.prenom || row.prenom.toString().trim() === '') {
        errors.push(`Ligne ${line}: Prénom manquant`);
      }
      
      // Email
      if (!row.email || row.email.toString().trim() === '') {
        errors.push(`Ligne ${line}: Email manquant`);
      } else {
        const email = row.email.toString().trim().toLowerCase();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        
        if (!emailRegex.test(email)) {
          errors.push(`Ligne ${line}: Email invalide (${email})`);
        } else if (emails.has(email)) {
          errors.push(`Ligne ${line}: Email dupliqué (${email})`);
        } else {
          emails.add(email);
        }
      }
      
      // ✅ Profil - validation stricte
      const profile = row.profile?.toString().toLowerCase().trim() || '';
      
      if (!profile) {
        errors.push(`Ligne ${line}: Profil manquant`);
      } else if (profile === '3' || profile === '1' || profile === 'gestionnaire' || profile === 'admin') {
        errors.push(`Ligne ${line}: Profil non autorisé (${profile}). Vous ne pouvez créer que des Contrôleurs ou des Agents.`);
      } else if (!validProfiles.includes(profile)) {
        errors.push(`Ligne ${line}: Profil invalide (${profile}). Utilisez: Contrôleur ou Agent`);
      }
      
      // ✅ Téléphone - INDICATIF OBLIGATOIRE (STRICT)
      if (row.phone && row.phone.toString().trim() !== '') {
        const phone = row.phone.toString().replace(/\s/g, '');
        
        // ❌ REJETTE TOUT CE QUI NE COMMENCE PAS PAR +
        if (!phone.startsWith('+')) {
          errors.push(`Ligne ${line}: ❌ Le téléphone doit commencer par + (indicatif international). Exemple: +22712345678`);
        } else if (!/^\+\d+$/.test(phone)) {
          errors.push(`Ligne ${line}: ❌ Format de téléphone invalide (${row.phone}). Utilisez uniquement des chiffres après le +`);
        }
      }
      
      // Statut (optionnel)
      if (row.statut && row.statut.toString().trim() !== '') {
        const status = row.statut.toString().toLowerCase().trim();
        if (!['actif', 'inactif', '1', '0'].includes(status)) {
          errors.push(`Ligne ${line}: Statut invalide (${status}). Utilisez: actif ou inactif`);
        }
      }
    });

    return errors;
  };

  // ✅ Importer les données
  const handleImport = async () => {
    if (!file || previewData.length === 0) {
      setError("Aucune donnée à importer.");
      return;
    }

    const validationErrors = validateData(previewData);
    if (validationErrors.length > 0) {
      setError(`Erreurs de validation :\n${validationErrors.slice(0, 10).join('\n')}`);
      return;
    }

    setImporting(true);
    setError("");
    
    const token = localStorage.getItem("access_token");
    if (!token) {
      setError("Session expirée. Veuillez vous reconnecter.");
      setImporting(false);
      return;
    }

    if (!currentSocieteId) {
      setError("Impossible de déterminer votre société. Contactez l'administrateur.");
      setImporting(false);
      return;
    }

    try {
      const results = {
        created_count: 0,
        duplicate_count: 0,
        error_count: 0,
        errors: [],
        details: []
      };

      // Pour chaque ligne
      for (let i = 0; i < previewData.length; i++) {
        const row = previewData[i];
        const lineNumber = i + 2;
        
        try {
          // ✅ Convertir les libellés en codes
          const profileCode = convertProfileToCode(row.profile);
          const statusCode = convertStatusToCode(row.statut);
          
          // Vérifier la conversion
          if (!profileCode) {
            throw new Error(`Profil invalide: ${row.profile}`);
          }

          // ✅ Normaliser le numéro de téléphone (indicatif obligatoire)
          let normalizedPhone = '';
          if (row.phone && row.phone.toString().trim() !== '') {
            try {
              normalizedPhone = normalizePhoneNumber(row.phone);
            } catch (phoneError) {
              throw new Error(`Téléphone: ${phoneError.message}`);
            }
          }

          console.log(`📝 Ligne ${lineNumber}:`, {
            profile_original: row.profile,
            profile_converti: profileCode,
            status_original: row.statut || 'non spécifié',
            status_converti: statusCode,
            phone: normalizedPhone
          });

          // ✅ Préparer les données pour l'endpoint create.php
          const userData = {
            nom: row.nom?.trim() || '',
            prenom: row.prenom?.trim() || '',
            email: row.email?.trim() || '',
            phone: normalizedPhone,                // ← Numéro avec indicatif (+227...)
            matricule_user: row.matricule?.trim() || '',
            profile: profileCode,                  // ← "2" ou "4"
            statut_user: statusCode,               // ← "1" ou "0"
            societe_id: currentSocieteId,          // ← Société du gestionnaire
            send_email: true
          };

          // Nettoyer les données
          Object.keys(userData).forEach(key => {
            if (typeof userData[key] === 'string') {
              userData[key] = userData[key].trim();
            }
          });

          // Appeler l'endpoint create.php
          const response = await axios.post(
            `${API_BASE_URL}/users/create.php`,
            userData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          if (response.data?.success) {
            results.created_count++;
            results.details.push({
              line: lineNumber,
              nom: `${row.prenom} ${row.nom}`,
              profile: row.profile,
              code: profileCode,
              status: 'success',
              message: '✅ Créé'
            });
          } else {
            results.error_count++;
            const errorMsg = response.data?.message || 'Erreur inconnue';
            results.errors.push(`Ligne ${lineNumber}: ${errorMsg}`);
            results.details.push({
              line: lineNumber,
              nom: `${row.prenom} ${row.nom}`,
              profile: row.profile,
              status: 'error',
              message: errorMsg
            });
          }
        } catch (err) {
          results.error_count++;
          
          let errorMessage = `Ligne ${lineNumber}: `;
          
          if (err.response?.status === 400) {
            errorMessage += err.response.data?.message || 'Données invalides';
            if (err.response.data?.errors) {
              errorMessage += ' (' + err.response.data.errors.join(', ') + ')';
            }
          } else if (err.response?.status === 409) {
            errorMessage += 'Email déjà utilisé';
            results.duplicate_count++;
          } else if (err.response?.status === 403) {
            errorMessage += 'Permission refusée';
          } else if (!err.response) {
            errorMessage += 'Serveur inaccessible';
          } else {
            errorMessage += err.message || 'Erreur technique';
          }
          
          results.errors.push(errorMessage);
          results.details.push({
            line: lineNumber,
            nom: `${row.prenom} ${row.nom}`,
            profile: row.profile,
            status: 'error',
            message: errorMessage
          });
        }

        // Petit délai pour éviter de surcharger le serveur
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Afficher les résultats
      setImportResults(results);
      
      if (results.created_count > 0) {
        setSuccessMsg(`✅ Importation terminée ! ${results.created_count} utilisateur(s) créé(s).`);
      } else {
        setError(`❌ Aucun utilisateur créé. ${results.error_count} erreur(s).`);
      }
      
      setStep(3);
      
    } catch (err) {
      console.error("Erreur import:", err);
      setError("Erreur lors du traitement de l'import.");
    } finally {
      setImporting(false);
    }
  };

  // Télécharger le rapport d'erreurs
  const downloadErrors = () => {
    if (!importResults?.errors || importResults.errors.length === 0) return;
    
    let errorContent = `=== RAPPORT D'ERREURS - IMPORT UTILISATEURS ===\n`;
    errorContent += `Date: ${new Date().toLocaleString()}\n`;
    errorContent += `Fichier: ${file?.name || 'Inconnu'}\n`;
    errorContent += `Total lignes: ${previewData.length}\n`;
    errorContent += `Créés: ${importResults.created_count}\n`;
    errorContent += `Erreurs: ${importResults.error_count}\n`;
    errorContent += `========================================\n\n`;
    
    importResults.details.forEach(detail => {
      errorContent += `[${detail.status === 'success' ? '✅' : '❌'}] Ligne ${detail.line} - ${detail.nom} (${detail.profile}): ${detail.message}\n`;
    });
    
    const blob = new Blob([errorContent], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapport_import_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Réinitialiser
  const handleReset = () => {
    setFile(null);
    setPreviewData([]);
    setError("");
    setSuccessMsg("");
    setImportResults(null);
    setStep(1);
  };

  // Retour à la liste
  const handleCancel = () => {
    navigate("/users");
  };

  // Vérifier les permissions
  if (!userInfo || String(userInfo.profile).trim() !== "3") {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Import d'utilisateurs</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/users">Utilisateurs</Link>
              </li>
              <li className="breadcrumb-item active">Import</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                width: '80px',
                height: '80px',
                backgroundColor: '#ffebee',
                color: COLORS.danger
              }}>
                <i className="bi bi-shield-lock" style={{ fontSize: '2rem' }}></i>
              </div>
              <h5 style={{ color: COLORS.primary, fontWeight: '600' }}>Permission refusée</h5>
              <p className="text-muted">
                Seuls les gestionnaires peuvent importer des utilisateurs.
              </p>
              <button
                className="btn mt-3 fw-semibold"
                onClick={() => navigate("/users")}
                style={{
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '10px 24px'
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  // Rendu principal
  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Import d'utilisateurs</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/users">Utilisateurs</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Import en masse</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <h5 className="card-title" style={{ 
              color: COLORS.primary,
              borderBottom: `2px solid ${COLORS.lightBg}`,
              paddingBottom: '16px',
              marginBottom: '24px'
            }}>
              <i className="bi bi-people-fill me-2" style={{ color: COLORS.secondary }}></i>
              Importer des utilisateurs
            </h5>

            {/* Messages */}
            {successMsg && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.success
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-check-circle me-2"></i>
                  <div className="flex-grow-1">{successMsg}</div>
                  <button type="button" className="btn-close" onClick={() => setSuccessMsg("")}></button>
                </div>
              </div>
            )}

            {error && (
              <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.danger,
                whiteSpace: 'pre-line'
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{error}</div>
                  <button type="button" className="btn-close" onClick={() => setError("")}></button>
                </div>
              </div>
            )}

            {/* Stepper */}
            <div className="mb-5">
              <div className="d-flex align-items-center justify-content-between mb-4">
                {[1, 2, 3].map((num) => (
                  <div key={num} className="d-flex align-items-center">
                    <div className={`rounded-circle d-flex align-items-center justify-content-center me-3`} style={{ 
                      width: '40px',
                      height: '40px',
                      backgroundColor: step >= num ? COLORS.primary : COLORS.lightBg,
                      color: step >= num ? 'white' : COLORS.darkText,
                      fontWeight: '600'
                    }}>
                      {num}
                    </div>
                    <div>
                      <h6 style={{ 
                        color: step >= num ? COLORS.primary : COLORS.darkText,
                        fontWeight: '600',
                        marginBottom: '4px'
                      }}>
                        {num === 1 ? 'Upload' : num === 2 ? 'Prévisualisation' : 'Résultats'}
                      </h6>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Étape 1: Upload */}
            {step === 1 && (
              <div className="text-center py-4">
                <div className="row justify-content-center">
                  <div className="col-md-8">
                    <div 
                      className="card border-dashed" 
                      style={{ 
                        borderRadius: '12px',
                        border: `2px dashed ${COLORS.accent}`,
                        backgroundColor: '#F8FBFF',
                        padding: '40px 20px',
                        cursor: 'pointer'
                      }}
                      onClick={() => document.getElementById('fileInput').click()}
                    >
                      <input
                        type="file"
                        id="fileInput"
                        accept=".csv"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                      <div className="text-center">
                        <i className="bi bi-cloud-arrow-up" style={{ fontSize: '3rem', color: COLORS.secondary }}></i>
                        <h6 style={{ color: COLORS.primary, fontWeight: '600', marginTop: '1rem' }}>
                          Cliquez pour sélectionner un fichier
                        </h6>
                        <p className="text-muted">ou glissez-déposez votre fichier CSV ici</p>
                        {file && (
                          <span className="badge" style={{ 
                            backgroundColor: '#e8f5e9',
                            color: '#2e7d32',
                            padding: '8px 16px',
                            borderRadius: '20px'
                          }}>
                            {file.name} ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="d-flex justify-content-center mt-4">
                      <button
                        className="btn fw-semibold"
                        onClick={downloadTemplate}
                        style={{
                          color: COLORS.primary,
                          borderColor: COLORS.primary,
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '10px 24px'
                        }}
                      >
                        <i className="bi bi-download me-2"></i>
                        Télécharger le modèle
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Étape 2: Prévisualisation */}
            {step === 2 && previewData.length > 0 && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h6 style={{ color: COLORS.primary, fontWeight: '600' }}>
                    {previewData.length} utilisateur(s) à importer
                  </h6>
                  <button
                    className="btn btn-sm fw-semibold"
                    onClick={handleReset}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      padding: '6px 12px'
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Changer de fichier
                  </button>
                </div>

                <div className="table-responsive mb-4" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table className="table table-hover table-sm">
                    <thead style={{ backgroundColor: '#F5F7FA', position: 'sticky', top: 0 }}>
                      <tr>
                        {Object.keys(previewData[0] || {}).map((header, index) => (
                          <th key={index} style={{ 
                            color: COLORS.primary,
                            fontWeight: '600',
                            fontSize: '0.85rem',
                            padding: '12px 8px'
                          }}>
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {Object.values(row).map((value, colIndex) => (
                            <td key={colIndex} style={{ 
                              fontSize: '0.85rem',
                              padding: '8px'
                            }}>
                              {value || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex justify-content-end gap-3 pt-4 border-top">
                  <button
                    className="btn fw-semibold"
                    onClick={handleReset}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 24px'
                    }}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Annuler
                  </button>
                  
                  <button
                    className="btn fw-semibold"
                    onClick={handleImport}
                    disabled={importing}
                    style={{
                      color: 'white',
                      backgroundColor: importing ? '#B0BEC5' : COLORS.primary,
                      borderColor: importing ? '#B0BEC5' : COLORS.primary,
                      borderRadius: '8px',
                      padding: '10px 32px'
                    }}
                  >
                    {importing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Importation...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-cloud-arrow-up me-2"></i>
                        Importer
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Étape 3: Résultats */}
            {step === 3 && importResults && (
              <div className="text-center py-4">
                <div className="mb-4">
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                    width: '80px',
                    height: '80px',
                    backgroundColor: importResults.created_count > 0 ? '#e8f5e9' : '#ffebee',
                    color: importResults.created_count > 0 ? COLORS.success : COLORS.danger
                  }}>
                    <i className={`bi ${importResults.created_count > 0 ? 'bi-check-circle' : 'bi-exclamation-triangle'}`} style={{ fontSize: '2rem' }}></i>
                  </div>
                  <h5 style={{ color: COLORS.primary, fontWeight: '600' }}>
                    {importResults.created_count > 0 ? 'Importation réussie !' : 'Échec de l\'importation'}
                  </h5>
                  
                  <div className="row justify-content-center mt-4">
                    <div className="col-md-8">
                      <div className="card mb-4">
                        <div className="card-body">
                          <div className="row">
                            <div className="col-4">
                              <h4 style={{ color: COLORS.success }}>{importResults.created_count}</h4>
                              <p className="small">Créés</p>
                            </div>
                            <div className="col-4">
                              <h4 style={{ color: COLORS.warning }}>{importResults.duplicate_count}</h4>
                              <p className="small">Doublons</p>
                            </div>
                            <div className="col-4">
                              <h4 style={{ color: COLORS.danger }}>{importResults.error_count}</h4>
                              <p className="small">Erreurs</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {importResults.errors.length > 0 && (
                        <div className="alert alert-warning text-start">
                          <strong>❌ Erreurs détectées :</strong>
                          <ul className="mb-2 mt-2 small">
                            {importResults.errors.slice(0, 5).map((err, i) => (
                              <li key={i}>{err}</li>
                            ))}
                          </ul>
                          <button
                            className="btn btn-sm"
                            onClick={downloadErrors}
                            style={{
                              color: COLORS.warning,
                              borderColor: COLORS.warning,
                              backgroundColor: 'transparent'
                            }}
                          >
                            <i className="bi bi-download me-1"></i>
                            Télécharger le rapport
                          </button>
                        </div>
                      )}

                      <div className="d-flex justify-content-center gap-3 mt-4">
                        <button
                          className="btn fw-semibold"
                          onClick={handleReset}
                          style={{
                            color: COLORS.primary,
                            borderColor: COLORS.primary,
                            backgroundColor: 'transparent',
                            borderRadius: '8px',
                            padding: '10px 24px'
                          }}
                        >
                          <i className="bi bi-plus-circle me-2"></i>
                          Nouvel import
                        </button>
                        
                        <button
                          className="btn fw-semibold"
                          onClick={handleCancel}
                          style={{
                            color: 'white',
                            backgroundColor: COLORS.primary,
                            borderColor: COLORS.primary,
                            borderRadius: '8px',
                            padding: '10px 24px'
                          }}
                        >
                          <i className="bi bi-list me-2"></i>
                          Voir la liste
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default UsersImport;